/**
 * @fileoverview Phone Number Module Component
 * 
 * A configurable phone number search and purchase module that can be used
 * across different contexts (onboarding, dashboard, admin) with context-specific
 * search filters and purchase workflows.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  PhoneNumberContext, 
  PurchaseFlow, 
  PhoneNumberEventType,
  DEFAULT_PHONE_NUMBER_CONFIG 
} from './types.js';
import PhoneNumberSearch from './components/PhoneNumberSearch.jsx';
import PurchaseConfirmationEnhanced from './components/PurchaseConfirmationEnhanced.jsx';
import SearchResults from './components/SearchResults.jsx';
import { validatePhoneNumberConfig } from './utils/validation.js';
import { createSearchCriteria, formatPhoneNumber } from './utils/helpers.js';
import { createPhoneNumberService } from './services/PhoneNumberService.js';

/**
 * Phone Number Module - Configurable phone number search and purchase
 * 
 * @param {Object} props - Component props
 * @param {PhoneNumberConfig} props.config - Module configuration
 * @param {ModuleContext} props.context - Shared module context
 * @param {function} props.onEvent - Event handler for module events
 * @param {string} props.className - Optional CSS class name
 * @returns {React.ReactElement} Phone number module component
 */
const PhoneNumberModule = ({ 
  config = DEFAULT_PHONE_NUMBER_CONFIG, 
  context, 
  onEvent, 
  className = '' 
}) => {
  // Validate configuration
  const validationResult = validatePhoneNumberConfig(config);
  if (!validationResult.valid) {
    console.error('Invalid phone number module configuration:', validationResult.errors);
    return (
      <div className={`phone-number-module-error ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Configuration Error</h3>
          <ul className="text-red-700 text-sm mt-2">
            {validationResult.errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  // Component state
  const [searchCriteria, setSearchCriteria] = useState(() => 
    createSearchCriteria(config.searchFilters)
  );
  const [searchResults, setSearchResults] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showPurchaseConfirmation, setShowPurchaseConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Refs for cleanup
  const abortControllerRef = useRef(null);
  const phoneNumberServiceRef = useRef(null);

  // Initialize phone number service
  useEffect(() => {
    if (context && context.apiClient) {
      phoneNumberServiceRef.current = createPhoneNumberService(context.apiClient, context);
    }
  }, [context]);

  // Event emission helper
  const emitEvent = useCallback((type, payload) => {
    if (onEvent) {
      onEvent({
        type,
        payload,
        source: 'phone-number-module',
        timestamp: Date.now()
      });
    }
  }, [onEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);



  /**
   * Handle search criteria changes
   */
  const handleSearchCriteriaChange = useCallback((field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  }, []);

  /**
   * Perform phone number search with enhanced Twilio integration
   */
  const handleSearch = useCallback(async () => {
    // Cancel any existing search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setError(null);
    setSearchResults([]);
    setSearchPerformed(false);

    emitEvent(PhoneNumberEventType.SEARCH_STARTED, { searchCriteria });

    try {
      // Build search parameters with proper Twilio parameter mapping
      const params = new URLSearchParams();
      
      // Map frontend search criteria to Twilio API parameters
      if (searchCriteria.country) {
        params.append('countryCode', searchCriteria.country);
      }
      if (searchCriteria.areaCode) {
        params.append('areaCode', searchCriteria.areaCode);
      }
      if (searchCriteria.city) {
        params.append('city', searchCriteria.city);
      }
      if (searchCriteria.region) {
        params.append('region', searchCriteria.region);
      }
      if (searchCriteria.postalCode) {
        params.append('postalCode', searchCriteria.postalCode);
      }
      if (searchCriteria.phoneExchange) {
        params.append('phoneExchange', searchCriteria.phoneExchange);
      }
      if (searchCriteria.numberPattern) {
        params.append('numberPattern', searchCriteria.numberPattern);
      }
      if (searchCriteria.letterPattern) {
        params.append('letterPattern', searchCriteria.letterPattern);
      }
      if (searchCriteria.capabilities && Array.isArray(searchCriteria.capabilities)) {
        params.append('capabilities', searchCriteria.capabilities.join(','));
      }

      // Add context-specific parameters
      if (config.maxResults) {
        params.append('limit', config.maxResults.toString());
      }

      // Determine API endpoint based on context
      const endpoint = config.context === PhoneNumberContext.ADMIN 
        ? '/api/admin/phone-numbers/search'
        : '/api/phone/search';

      // Make API request with enhanced error handling
      const response = await context.apiClient.get(
        `${endpoint}?${params}`,
        { 
          signal: abortControllerRef.current.signal,
          timeout: 15000, // 15 second timeout for search
          requestType: 'phone-search'
        }
      );

      const results = response.results || [];
      setSearchResults(results);
      setSearchPerformed(true);

      emitEvent(PhoneNumberEventType.SEARCH_COMPLETED, { 
        searchCriteria, 
        resultCount: results.length,
        results: results.slice(0, 5), // Only send first 5 for event logging
        cached: response.cached || false
      });

      if (results.length === 0) {
        setError('No phone numbers found matching your criteria. Try adjusting your search parameters.');
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Search was cancelled, don't update state
      }

      console.error('Phone number search failed:', err);
      
      // Enhanced error handling for different error types
      let errorMessage = 'Failed to search for phone numbers';
      
      if (err.status === 502) {
        errorMessage = 'Phone number search service is temporarily unavailable. Please try again.';
      } else if (err.status === 429) {
        errorMessage = 'Too many search requests. Please wait a moment before searching again.';
      } else if (err.status === 401) {
        errorMessage = 'Authentication required. Please sign in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      emitEvent(PhoneNumberEventType.SEARCH_FAILED, { 
        searchCriteria, 
        error: errorMessage,
        errorCode: err.status || 'UNKNOWN',
        errorType: err.category || 'network'
      });

      // Show error toast if available
      if (context.toast) {
        context.toast.error(errorMessage);
      }

    } finally {
      setIsSearching(false);
      abortControllerRef.current = null;
    }
  }, [searchCriteria, config, context, emitEvent]);

  /**
   * Check if search can be performed
   */
  const canSearch = useCallback(() => {
    // Check required filters first
    const requiredFilters = config.searchFilters.filter(filter => filter.required);
    const hasRequiredFilters = requiredFilters.every(filter => {
      const value = searchCriteria[filter.type];
      return value && value.toString().trim();
    });

    // If there are required filters and they're not all filled, can't search
    if (requiredFilters.length > 0 && !hasRequiredFilters) {
      return false;
    }

    // Check if at least one search criteria is provided
    const hasSearchCriteria = Object.values(searchCriteria).some(value => 
      value && value.toString().trim()
    );

    return hasSearchCriteria;
  }, [searchCriteria, config.searchFilters]);

  /**
   * Handle phone number selection
   */
  const handleNumberSelect = useCallback((number) => {
    setSelectedNumber(number);
    setError(null);

    emitEvent(PhoneNumberEventType.NUMBER_SELECTED, { number });

    // Handle different purchase flows
    switch (config.purchaseFlow) {
      case PurchaseFlow.IMMEDIATE:
        handlePurchase(number);
        break;
      case PurchaseFlow.CONFIRMATION:
        setShowPurchaseConfirmation(true);
        break;
      case PurchaseFlow.ADMIN_APPROVAL:
        // For admin approval, just notify parent
        if (config.onNumberSelected) {
          config.onNumberSelected(number);
        }
        break;
      default:
        setShowPurchaseConfirmation(true);
    }
  }, [config]);

  /**
   * Handle phone number purchase with enhanced billing integration
   */
  const handlePurchase = useCallback(async (number) => {
    // Use the passed number or fall back to selectedNumber
    const phoneNumber = number || selectedNumber;
    if (!phoneNumber) return;

    setIsPurchasing(true);
    setError(null);
    setShowPurchaseConfirmation(false);

    emitEvent(PhoneNumberEventType.PURCHASE_STARTED, { number: phoneNumber });

    try {
      // First, validate purchase eligibility if enhanced validation is available
      if (config.validateBeforePurchase !== false) {
        try {
          const validationResponse = await context.apiClient.post('/api/phone/purchase/validate', {
            phoneNumber: phoneNumber.phoneNumber
          }, {
            timeout: 10000,
            requestType: 'phone-validation'
          });

          if (!validationResponse.canPurchase) {
            const errorMessage = validationResponse.errors?.[0] || 'Purchase not allowed';
            setError(errorMessage);
            
            emitEvent(PhoneNumberEventType.PURCHASE_FAILED, { 
              number: phoneNumber, 
              error: errorMessage,
              validationErrors: validationResponse.errors,
              billingContext: validationResponse.billingContext
            });

            if (context.toast) {
              context.toast.error(errorMessage);
            }
            return;
          }

          // Show billing warnings if any
          if (validationResponse.warnings && validationResponse.warnings.length > 0) {
            validationResponse.warnings.forEach(warning => {
              if (context.toast) {
                context.toast.warning(warning);
              }
            });
          }
        } catch (validationError) {
          console.warn('Purchase validation failed, proceeding with purchase:', validationError);
          // Continue with purchase even if validation fails
        }
      }

      // Prepare purchase request with enhanced data
      const purchaseRequest = {
        phoneNumber: phoneNumber.phoneNumber
      };

      // Add billing context if available
      if (config.billingContext) {
        purchaseRequest.billingContext = config.billingContext;
      }

      // Add purchase metadata for tracking
      purchaseRequest.metadata = {
        context: config.context,
        searchCriteria: searchCriteria,
        moduleVersion: '1.0.0',
        timestamp: new Date().toISOString()
      };

      // Determine API endpoint based on context
      const endpoint = config.context === PhoneNumberContext.ADMIN
        ? '/api/admin/phone-numbers/purchase'
        : '/api/phone/purchase';

      // Make purchase request with extended timeout for Twilio processing
      const result = await context.apiClient.post(endpoint, purchaseRequest, {
        timeout: 30000, // 30 second timeout for purchase
        requestType: 'phone-purchase'
      });

      // Handle successful purchase
      emitEvent(PhoneNumberEventType.PURCHASE_COMPLETED, { 
        number: phoneNumber, 
        result,
        billing: result.billing || null,
        twilioId: result.id || null
      });

      // Show success toast with billing information
      let successMessage = `Phone number ${formatPhoneNumber(phoneNumber.phoneNumber)} purchased successfully!`;
      if (result.billing && result.billing.charged && result.billing.cost > 0) {
        const cost = (result.billing.cost / 100).toFixed(2);
        successMessage += ` (Charged: $${cost})`;
      } else if (result.billing && result.billing.isIncluded) {
        successMessage += ' (Included in your plan)';
      }

      if (context.toast) {
        context.toast.success(successMessage);
      }

      // Notify parent component with enhanced result data
      if (config.onPurchaseComplete) {
        config.onPurchaseComplete({
          ...result,
          phoneNumber: phoneNumber.phoneNumber,
          friendlyName: result.friendlyName || phoneNumber.friendlyName,
          purchaseContext: config.context,
          purchaseTimestamp: new Date().toISOString()
        });
      }

      // Remove purchased number from search results
      setSearchResults(prev => 
        prev.filter(n => n.phoneNumber !== phoneNumber.phoneNumber)
      );

      // Clear selection
      setSelectedNumber(null);

    } catch (err) {
      console.error('Phone number purchase failed:', err);
      
      // Enhanced error handling for different error types
      let errorMessage = 'Failed to purchase phone number';
      
      if (err.status === 502) {
        errorMessage = 'Phone number purchase service is temporarily unavailable. Please try again.';
      } else if (err.status === 409) {
        errorMessage = 'This phone number is no longer available. Please select a different number.';
      } else if (err.status === 402) {
        errorMessage = 'Insufficient funds. Please add money to your account or upgrade your plan.';
      } else if (err.status === 429) {
        errorMessage = 'Too many purchase requests. Please wait a moment before trying again.';
      } else if (err.status === 401) {
        errorMessage = 'Authentication required. Please sign in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      emitEvent(PhoneNumberEventType.PURCHASE_FAILED, { 
        number: phoneNumber, 
        error: errorMessage,
        errorCode: err.status || 'UNKNOWN',
        errorType: err.category || 'network',
        billingContext: err.billingContext || null
      });

      // Show error toast
      if (context.toast) {
        context.toast.error(errorMessage);
      }

    } finally {
      setIsPurchasing(false);
    }
  }, [selectedNumber, config, context, emitEvent, searchCriteria]);

  /**
   * Handle purchase cancellation
   */
  const handlePurchaseCancel = useCallback(() => {
    setShowPurchaseConfirmation(false);
    setSelectedNumber(null);

    emitEvent(PhoneNumberEventType.PURCHASE_CANCELLED, { 
      number: selectedNumber 
    });
  }, [selectedNumber, emitEvent]);

  /**
   * Handle skip option (for onboarding)
   */
  const handleSkip = useCallback(() => {
    if (config.onPurchaseComplete) {
      config.onPurchaseComplete({ skipped: true });
    }
  }, [config]);



  return (
    <div className={`phone-number-module ${className}`} data-testid="phone-number-module">
      {/* Search Interface */}
      <PhoneNumberSearch
        config={config}
        searchCriteria={searchCriteria}
        onCriteriaChange={handleSearchCriteriaChange}
        onSearch={handleSearch}
        canSearch={canSearch()}
        isSearching={isSearching}
        error={error}
      />

      {/* Search Results */}
      {(searchPerformed || searchResults.length > 0) && (
        <SearchResults
          results={searchResults}
          isSearching={isSearching}
          onNumberSelect={handleNumberSelect}
          isPurchasing={isPurchasing}
          selectedNumber={selectedNumber}
          config={config}
          searchPerformed={searchPerformed}
        />
      )}

      {/* Purchase Confirmation Modal */}
      {showPurchaseConfirmation && selectedNumber && (
        <PurchaseConfirmationEnhanced
          number={selectedNumber}
          config={config}
          onConfirm={handlePurchase}
          onCancel={handlePurchaseCancel}
          isPurchasing={isPurchasing}
        />
      )}

      {/* Skip Option for Onboarding */}
      {config.showSkipOption && config.context === PhoneNumberContext.ONBOARDING && (
        <div className="text-center mt-6">
          <button
            onClick={handleSkip}
            className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
            disabled={isPurchasing}
          >
            Skip for now - I'll add a phone number later
          </button>
        </div>
      )}
    </div>
  );
};

// Static method for getting default configuration
PhoneNumberModule.getDefaultConfig = () => DEFAULT_PHONE_NUMBER_CONFIG;

// Display name for debugging
PhoneNumberModule.displayName = 'PhoneNumberModule';

export default PhoneNumberModule;
